import { SimpleModal, Flex } from '@sequencehq/core-components'
import { type ReactNode } from 'react'
import Spinner from 'components/Loading/Spinner'
import { SteppedModal } from './SteppedModal'
import type { ValidationItem } from './useSteppedModal'

type Props = {
  onClose: () => void
  onConfirm: () => Promise<void>
  submitting: boolean
  submitDisabled?: boolean
  /** True while any validation source is still resolving. */
  loading: boolean
  /**
   * A pre-stepper blocking dialog (e.g. Avalara / lock-date) shown instead of
   * the stepper when the action can't proceed and can't be remediated here.
   */
  blocker?: ReactNode
  /** Remediation steps to walk the user through before confirming. */
  validation: ValidationItem[]
  validationSummaryText: string
  confirmationSummaryText: ReactNode
  confirmTitleText: string
  confirmActionText: string
}

/**
 * Entity-agnostic send/issue confirmation. Callers assemble the validations
 * (tax rates, accounting lock date, ...) and pass them in; this owns the
 * loading gate, the blocker short-circuit, and the stepper.
 */
export const SendConfirmationModal = ({
  onClose,
  onConfirm,
  submitting,
  submitDisabled,
  loading,
  blocker,
  validation,
  validationSummaryText,
  confirmationSummaryText,
  confirmTitleText,
  confirmActionText
}: Props) => {
  if (loading) {
    return (
      <ConfirmationLoadingModal onClose={onClose} title={confirmTitleText} />
    )
  }

  if (blocker) {
    return <>{blocker}</>
  }

  return (
    <SteppedModal
      canSkip={false}
      onClose={onClose}
      onFinalConfirm={onConfirm}
      submitting={submitting}
      submitDisabled={submitDisabled}
      validation={validation}
      validationSummaryText={validationSummaryText}
      confirmationSummaryText={confirmationSummaryText}
      confirmTitleText={confirmTitleText}
      confirmActionText={confirmActionText}
    />
  )
}

const ConfirmationLoadingModal = ({
  onClose,
  title
}: {
  onClose: () => void
  title: string
}) => {
  return (
    <SimpleModal
      variant="dialog"
      isOpen
      title={title}
      onClose={onClose}
      showFooter={false}
      data-testid="sendConfirmation.loading"
    >
      <Flex minHeight="96px" alignItems="center" justifyContent="center">
        <Spinner />
      </Flex>
    </SimpleModal>
  )
}
